import { ComponentProps, FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Svg } from '@altermaind/web/ui';
import {
  PaginatedTable,
  usePaginatedTable,
  Column,
  StyledTable,
  ValueInTable,
} from '@/_shared/components';
import { Routes } from '@/_shared/constants/routes';
import { GenericApiError } from '@/_shared/types/apiError';
import { getDepartmentTeamMembersCall, Member } from '../../state';

type Props = {
  loading: boolean;
  hidePagination?: boolean;
  error: GenericApiError | undefined;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  drawer: ComponentProps<typeof StyledTable>['drawer'];
};

export const TableArea: FC<Props> = ({ paginedProps, drawer, loading, error, hidePagination }) => {
  const t = useTranslations('people.tableEmployees');
  const navigate = useNavigate();

  const [selectedMember, setSelectedMember] = useState<Member | undefined>();

  const goToDetail = (id: string) => {
    navigate(Routes.PeopleDetail.replace(':peopleId', id));
  };

  const columns: Column[] = [
    {
      key: 'firstName',
      title: t('firstName'),
      renderValue: (item: Member) => <ValueInTable>{item.firstName || '-'}</ValueInTable>,
    },
    {
      key: 'lastName',
      title: t('lastName'),
      renderValue: (item: Member) => <ValueInTable>{item.lastName || '-'}</ValueInTable>,
    },
    {
      key: 'email',
      title: t('email'),
      renderValue: (item: Member) => <ValueInTable>{item.email || '-'}</ValueInTable>,
    },
    {
      key: 'role',
      title: t('role'),
      renderValue: (item: Member) => <ValueInTable>{item.role || '-'}</ValueInTable>,
    },
    {
      key: 'edit',
      title: '',
      width: '60px',
      renderValue: (item: Member) =>
        item.email === selectedMember?.email ? (
          <Box
            display='flex'
            flexDirection='row'
            alignItems='flex-end'
            justifyContent='flex-end'
            width='100%'
            style={{ cursor: 'pointer' }}
            onClick={() => item.id && goToDetail(item.id)}
          >
            <Svg name='arrowRight' size='s' />
          </Box>
        ) : null,
    },
  ];

  useEffect(() => {
    setSelectedMember(undefined);
  }, []);

  return (
    <StyledTable
      error={!!error}
      loading={loading}
      hasElement={selectedMember !== undefined}
      drawer={drawer}
    >
      <PaginatedTable
        header-data-testid='table-team-header'
        row-data-testid='table-Member-row'
        columns={columns}
        pageSize={1000}
        call={{ ...getDepartmentTeamMembersCall }}
        onRowClick={(employee: Member) => {
          goToDetail(employee.id);
        }}
        onHoverRow={(employee: Member) => {
          setSelectedMember(employee);
        }}
        rowWrapperProps={(employee: Member) => {
          return employee.id === selectedMember?.id
            ? {
                backgroundColor: 'grey/dark/background',
                borderRadius: '10px',
                position: 'relative',
                cursor: 'pointer !important',
              }
            : {};
        }}
        paginedProps={paginedProps}
        skeletonProps={{
          hasHeaderRow1: false,
          hasHeaderRow2: true,
          rows: 1,
        }}
        hasHover
        hidePagination={hidePagination}
      />
    </StyledTable>
  );
};
